import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box
} from '@chakra-ui/react';
import { DetailedListingDataSuggestionGrid, BulkSelectionBar, BulkEditButton } from "components";
import { MdCheck, MdClose } from "react-icons/md";
import {
  useDetailedStatusListingDataSuggestions,
  useApproveListingDataSuggestion,
  useRejectListingDataSuggestion,
  useBulkApproveListingDataSuggestions,
  useBulkRejectListingDataSuggestions
} from "hooks/queries/useListingDataSuggestions";
import { useArtists } from "hooks/queries/useArtists";
import { ProcessStatus } from "models/types";
import { ListingDataSuggestionModel } from "models";

export function ListingDataSuggestionsOverview() {
  const [selectedStatus, setSelectedStatus] = useState<ProcessStatus>(ProcessStatus.PENDING);
  const [page, setPage] = useState(1);
  const { data: suggestionsPagination, isLoading } = useDetailedStatusListingDataSuggestions(selectedStatus, page);
  const { data: artistsData } = useArtists();
  const approveSuggestionMutation = useApproveListingDataSuggestion();
  const rejectSuggestionMutation = useRejectListingDataSuggestion();
  const bulkApproveMutation = useBulkApproveListingDataSuggestions();
  const bulkRejectMutation = useBulkRejectListingDataSuggestions();

  // Selection mode state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedSuggestions, setSelectedSuggestions] = useState<ListingDataSuggestionModel[]>([]);
  const visibleSuggestionsRef = useRef<ListingDataSuggestionModel[]>([]);

  // Get suggestions from pagination data
  const suggestionList = suggestionsPagination?.data || [];
  const hasPendingSuggestions = suggestionList.some(s => s.isPending());
  const artistsList = artistsData || [];

  const handleStatusChange = (status: ProcessStatus) => {
    setSelectedStatus(status);
    setPage(1);
  };

  const handleApprove = useCallback((suggestionId: string) => {
    approveSuggestionMutation.mutate({ listing_data_suggestion_id: suggestionId });
  }, [approveSuggestionMutation]);

  const handleReject = useCallback((suggestionId: string) => {
    rejectSuggestionMutation.mutate({ listing_data_suggestion_id: suggestionId });
  }, [rejectSuggestionMutation]);

  const isActionLoading = approveSuggestionMutation.isPending || rejectSuggestionMutation.isPending;

  // Selection mode handlers
  const handleEnterSelectionMode = useCallback(() => {
    if (!hasPendingSuggestions) return;
    setSelectionMode(true);
    setSelectedSuggestions([]);
  }, [hasPendingSuggestions]);

  const handleExitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedSuggestions([]);
  }, []);

  const handleSuggestionSelect = useCallback((suggestion: ListingDataSuggestionModel) => {
    setSelectedSuggestions(prev => {
      const exists = prev.some(s => s.id === suggestion.id);
      if (exists) {
        return prev.filter(s => s.id !== suggestion.id);
      }
      return [...prev, suggestion];
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const pendingSuggestions = visibleSuggestionsRef.current.filter(s => s.isPending());
    setSelectedSuggestions(pendingSuggestions);
  }, []);

  const handleFilteredSuggestionsChange = useCallback((suggestions: ListingDataSuggestionModel[]) => {
    visibleSuggestionsRef.current = suggestions;
  }, []);

  const handleBulkApprove = useCallback(async () => {
    if (selectedSuggestions.length === 0) return;
    const ids = selectedSuggestions.map(s => s.id);
    await bulkApproveMutation.mutateAsync(ids);
    handleExitSelectionMode();
  }, [selectedSuggestions, bulkApproveMutation, handleExitSelectionMode]);

  const handleBulkReject = useCallback(async () => {
    if (selectedSuggestions.length === 0) return;
    const ids = selectedSuggestions.map(s => s.id);
    await bulkRejectMutation.mutateAsync(ids);
    handleExitSelectionMode();
  }, [selectedSuggestions, bulkRejectMutation, handleExitSelectionMode]);

  // Keep selection mode consistent with status changes
  useEffect(() => {
    if (!hasPendingSuggestions) {
      setSelectionMode(false);
      setSelectedSuggestions([]);
    }
  }, [hasPendingSuggestions]);

  const pendingVisibleCount = visibleSuggestionsRef.current.filter(s => s.isPending()).length;

  return (
    <>
      <Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh" mx="auto">
        <DetailedListingDataSuggestionGrid
          suggestions={suggestionList}
          isLoading={isLoading}
          onApprove={handleApprove}
          onReject={handleReject}
          pagination={suggestionsPagination}
          onPageChange={setPage}
          defaultStatus={selectedStatus}
          onStatusChange={handleStatusChange}
          isActionLoading={isActionLoading}
          availableArtists={artistsList}
          selectionMode={selectionMode}
          selectedSuggestions={selectedSuggestions}
          onSuggestionSelect={handleSuggestionSelect}
          onFilteredSuggestionsChange={handleFilteredSuggestionsChange}
        />
      </Box>

      <BulkEditButton
        onClick={handleEnterSelectionMode}
        isVisible={!selectionMode && hasPendingSuggestions}
      />

      {/* Selection Bar - shown when in selection mode */}
      {selectionMode && (
        <BulkSelectionBar
          selectedCount={selectedSuggestions.length}
          totalCount={pendingVisibleCount}
          onSelectAll={handleSelectAll}
          actions={[
            {
              label: 'Approve',
              colorScheme: 'green',
              icon: MdCheck,
              onClick: handleBulkApprove
            },
            {
              label: 'Reject',
              colorScheme: 'red',
              icon: MdClose,
              onClick: handleBulkReject
            }
          ]}
          onCancel={handleExitSelectionMode}
          isLoading={bulkApproveMutation.isPending || bulkRejectMutation.isPending}
          entityName="listing data suggestion"
        />
      )}
    </>
  );
}

export default ListingDataSuggestionsOverview;
